(function () {
  'use strict';

  var config = window.BahimiMotionConfig || {};
  var root = document.documentElement;
  var reducedQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 749px)');
  var finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var observer = null;
  var lenis = null;
  var gsapPromise = null;
  var lenisPromise = null;
  var magneticElements = new WeakSet();
  var scrollRefreshRaf = null;
  var mutationRaf = null;
  var mutationRoots = [];
  var revealSelector = '.bh-reveal, [data-bh-motion-reveal], [data-bh-reveal]';

  function motionAllowed() {
    if (config.enabled === false || reducedQuery.matches) return false;
    if (mobileQuery.matches && config.mobileMotion === false) return false;
    return true;
  }

  function loadScript(id, src, readyTest) {
    return new Promise(function (resolve, reject) {
      if (readyTest()) {
        resolve();
        return;
      }

      var existing = document.getElementById(id);
      if (existing) {
        existing.addEventListener('load', resolve, { once: true });
        existing.addEventListener('error', reject, { once: true });
        return;
      }

      var script = document.createElement('script');
      script.id = id;
      script.src = src;
      script.defer = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', reject, { once: true });
      document.head.appendChild(script);
    });
  }

  function whenGsap() {
    if (!motionAllowed()) return Promise.reject(new Error('Bahimi motion is disabled'));
    if (gsapPromise) return gsapPromise;

    gsapPromise = loadScript('bh-gsap-core', config.gsapUrl, function () {
      return Boolean(window.gsap);
    })
      .then(function () {
        return loadScript('bh-gsap-scroll-trigger', config.scrollTriggerUrl, function () {
          return Boolean(window.ScrollTrigger);
        });
      })
      .then(function () {
        window.gsap.registerPlugin(window.ScrollTrigger);
        root.classList.add('bh-gsap-ready');
        root.classList.remove('bh-gsap-error');
        if (lenis) lenis.on('scroll', window.ScrollTrigger.update);
        document.dispatchEvent(new CustomEvent('bahimi:gsap-ready'));
        scheduleScrollRefresh();
        return { gsap: window.gsap, ScrollTrigger: window.ScrollTrigger };
      })
      .catch(function (error) {
        root.classList.add('bh-gsap-error');
        gsapPromise = null;
        throw error;
      });

    return gsapPromise;
  }

  function selectWithin(scope, selector) {
    var elements = [];
    if (scope.nodeType === 1 && scope.matches(selector)) elements.push(scope);
    return elements.concat(Array.prototype.slice.call(scope.querySelectorAll(selector)));
  }

  function revealImmediately(scope) {
    selectWithin(scope, revealSelector).forEach(function (element) {
      element.classList.add('bh-revealed');
    });
  }

  function ensureObserver() {
    if (observer || !('IntersectionObserver' in window)) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('bh-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -7% 0px' }
    );

    return observer;
  }

  function initReveals(scope) {
    var context = scope || document;
    var elements = selectWithin(context, revealSelector);

    if (!motionAllowed() || !config.cardReveals || !('IntersectionObserver' in window)) {
      elements.forEach(function (element) {
        element.classList.add('bh-revealed');
      });
      return;
    }

    var io = ensureObserver();
    elements.forEach(function (element, index) {
      if (element.classList.contains('bh-revealed')) return;
      var motionIndex = Number(element.dataset.bhMotionIndex || index);
      var boundedIndex = Math.max(0, Math.min(motionIndex % 8, 7));
      element.style.setProperty('--bh-motion-delay', 'calc(var(--bh-motion-stagger) * ' + boundedIndex + ')');
      io.observe(element);
    });
  }

  function initMagnetic(scope) {
    if (!motionAllowed() || !config.magneticCtas || !finePointerQuery.matches) return;
    var context = scope || document;

    selectWithin(context, '[data-bh-magnetic]').forEach(function (element) {
      if (magneticElements.has(element)) return;
      magneticElements.add(element);

      element.addEventListener('pointermove', function (event) {
        var rect = element.getBoundingClientRect();
        var x = (event.clientX - rect.left - rect.width / 2) * 0.12;
        var y = (event.clientY - rect.top - rect.height / 2) * 0.16;
        element.style.setProperty('--bh-magnetic-x', x.toFixed(2) + 'px');
        element.style.setProperty('--bh-magnetic-y', y.toFixed(2) + 'px');
        element.classList.add('is-bh-magnetic-active');
      });

      element.addEventListener('pointerleave', function () {
        element.style.setProperty('--bh-magnetic-x', '0px');
        element.style.setProperty('--bh-magnetic-y', '0px');
        element.classList.remove('is-bh-magnetic-active');
      });
    });
  }

  function initLenis() {
    if (
      lenisPromise ||
      !motionAllowed() ||
      !config.smoothScroll ||
      mobileQuery.matches ||
      !finePointerQuery.matches ||
      (window.Shopify && window.Shopify.designMode)
    ) {
      return lenisPromise;
    }

    lenisPromise = loadScript('bh-lenis', config.lenisUrl, function () {
      return Boolean(window.Lenis);
    }).then(function () {
      lenis = new window.Lenis({
        autoRaf: true,
        anchors: true,
        smoothWheel: true,
        duration: config.intensity === 'expressive' ? 1.05 : 0.88
      });
      window.BahimiMotion.lenis = lenis;
      if (window.ScrollTrigger) lenis.on('scroll', window.ScrollTrigger.update);
      return lenis;
    });

    return lenisPromise;
  }

  function track(name, detail) {
    var payload = Object.assign({ event: name }, detail || {});
    document.dispatchEvent(new CustomEvent('bahimi:analytics', { detail: payload }));
    if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload);
  }

  function initAnalytics() {
    document.addEventListener('click', function (event) {
      var target = event.target.closest(
        '[data-bh-hc-label], [data-bh-card-link], [data-bh-quickview], [data-bh-add], [data-bh-lookbook] a'
      );
      if (!target) return;

      if (target.matches('[data-bh-hc-label]')) track('bahimi_hero_cta_click');
      else if (target.matches('[data-bh-card-link]')) track('bahimi_product_card_click');
      else if (target.matches('[data-bh-quickview]')) track('bahimi_quick_view_open');
      else if (target.matches('[data-bh-add]')) track('bahimi_quick_add');
      else track('bahimi_lookbook_click');
    });
  }

  function initProductPageTransitions() {
    var transitionStorageKey = 'bahimi:pdp-transition';

    function isPlainPrimaryClick(event, link) {
      return !(
        event.defaultPrevented ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        (typeof event.button === 'number' && event.button !== 0) ||
        link.target === '_blank' ||
        link.hasAttribute('download')
      );
    }

    function readTransitionRecord() {
      try {
        var record = JSON.parse(window.sessionStorage.getItem(transitionStorageKey));
        if (!record || !record.handle || Date.now() - Number(record.startedAt || 0) > 30 * 60 * 1000) {
          return null;
        }
        return record;
      } catch (error) {
        return null;
      }
    }

    function writeTransitionRecord(record) {
      try {
        window.sessionStorage.setItem(transitionStorageKey, JSON.stringify(record));
      } catch (error) {}
    }

    function clearTransitionRecord() {
      try {
        window.sessionStorage.removeItem(transitionStorageKey);
      } catch (error) {}
    }

    function clearNamedElements(preserve) {
      document
        .querySelectorAll('[data-bh-pdp-transition-source], [data-bh-pdp-transition-return-target]')
        .forEach(function (element) {
          if (element === preserve) return;
          element.removeAttribute('data-bh-pdp-transition-source');
          element.removeAttribute('data-bh-pdp-transition-return-target');
          element.style.removeProperty('view-transition-name');
        });
      document.querySelectorAll('.bh-card__transition-image').forEach(function (image) {
        if (image !== preserve) image.remove();
      });
    }

    function visibleCardImage(media) {
      if (!media) return null;
      var fade = media.querySelector('[data-bh-fade].is-visible');
      if (fade && (fade.currentSrc || fade.getAttribute('src'))) return fade;

      var primary = media.querySelector('.bh-card__img--primary');
      var hover = media.querySelector('[data-bh-hover]');
      if (
        primary &&
        hover &&
        (hover.currentSrc || hover.getAttribute('src')) &&
        parseFloat(window.getComputedStyle(primary).opacity || '1') < 0.5
      ) {
        return hover;
      }
      return primary || hover;
    }

    function createCardTransitionImage(card) {
      var media = card ? card.querySelector('.bh-card__media') : null;
      var existing = media ? media.querySelector('.bh-card__transition-image') : null;
      if (existing) return existing;

      var source = visibleCardImage(media);
      if (!media || !source) return null;

      var src = source.currentSrc || source.getAttribute('src');
      if (!src) return null;

      var clone = document.createElement('img');
      var styles = window.getComputedStyle(source);
      clone.className = 'bh-card__transition-image';
      clone.src = src;
      clone.alt = '';
      clone.setAttribute('aria-hidden', 'true');
      clone.style.objectPosition = styles.objectPosition;
      media.appendChild(clone);
      return clone;
    }

    function currentPdpImage() {
      var activeThumb = null;
      var activeThumbs = document.querySelectorAll('[data-bh-car] [data-bh-thumb].is-active');
      for (var index = 0; index < activeThumbs.length; index += 1) {
        if (window.getComputedStyle(activeThumbs[index]).display !== 'none') {
          activeThumb = activeThumbs[index];
          break;
        }
      }
      var slide = null;
      if (activeThumb) {
        slide = document.querySelector(
          '[data-bh-car] [data-bh-slide="' + activeThumb.getAttribute('data-bh-thumb') + '"]'
        );
      }
      slide = slide || document.querySelector('[data-bh-car] [data-bh-slide]:not([style*="display: none"])');
      return slide ? slide.querySelector('img') : null;
    }

    function productHandleFromPath() {
      var match = window.location.pathname.match(/^\/products\/([^/]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }

    function cardForHandle(handle) {
      var cards = document.querySelectorAll('[data-bh-product-handle]');
      for (var index = 0; index < cards.length; index += 1) {
        if (cards[index].getAttribute('data-bh-product-handle') === handle) return cards[index];
      }
      return null;
    }

    function prepareNamedSource(element) {
      if (!element) return false;
      clearNamedElements(element);
      document.querySelectorAll('.bhp-car__image--transition-target').forEach(function (target) {
        target.classList.remove('bhp-car__image--transition-target');
      });
      element.setAttribute('data-bh-pdp-transition-source', '');
      element.style.setProperty('view-transition-name', 'bh-product-media');
      root.classList.add('bh-pdp-transitioning');
      return true;
    }

    window.addEventListener('pageswap', function (event) {
      if (
        event.viewTransition &&
        !document.querySelector('[data-bh-pdp-transition-source]')
      ) {
        event.viewTransition.skipTransition();
      }
    });

    document.addEventListener(
      'click',
      function (event) {
        var link = event.target.closest('[data-bh-pdp-transition]');
        if (!link) return;
        if (!isPlainPrimaryClick(event, link)) return;

        var card = link.closest('[data-bh-card]');
        var image = createCardTransitionImage(card);
        if (!prepareNamedSource(image)) return;
        if (card) card.classList.add('is-bh-pdp-transitioning');
        writeTransitionRecord({
          direction: 'open',
          handle: card ? card.getAttribute('data-bh-product-handle') : link.getAttribute('data-handle'),
          originUrl: window.location.href,
          startedAt: Date.now()
        });
      },
      true
    );

    document.addEventListener(
      'click',
      function (event) {
        var closeLink = event.target.closest('[data-bh-pdp-close]');
        if (!closeLink || !isPlainPrimaryClick(event, closeLink)) return;

        var record = readTransitionRecord() || {};
        var image = currentPdpImage();
        if (!prepareNamedSource(image)) return;
        writeTransitionRecord({
          direction: 'close',
          handle: record.handle || productHandleFromPath(),
          originUrl: record.originUrl || closeLink.href,
          startedAt: Date.now()
        });
      },
      true
    );

    window.addEventListener('pagereveal', function (event) {
      if (!event.viewTransition) return;
      var record = readTransitionRecord();
      if (!record) return;

      var target = null;
      if (record.direction === 'close') {
        var card = cardForHandle(record.handle);
        target = createCardTransitionImage(card);
        if (card) card.classList.add('is-bh-pdp-transitioning');
        if (target) {
          target.setAttribute('data-bh-pdp-transition-return-target', '');
          target.style.setProperty('view-transition-name', 'bh-product-media');
        }
      } else if (record.direction === 'open') {
        target = currentPdpImage();
        document.querySelectorAll('.bhp-car__image--transition-target').forEach(function (image) {
          image.classList.remove('bhp-car__image--transition-target');
        });
        if (target) target.classList.add('bhp-car__image--transition-target');
      }

      if (!target) {
        event.viewTransition.skipTransition();
        root.classList.remove('bh-pdp-transitioning');
        if (record.direction === 'close') clearTransitionRecord();
        return;
      }

      root.classList.add('bh-pdp-transitioning');
      event.viewTransition.finished
        .catch(function () {})
        .then(function () {
          root.classList.remove('bh-pdp-transitioning');
          document.querySelectorAll('.is-bh-pdp-transitioning').forEach(function (element) {
            element.classList.remove('is-bh-pdp-transitioning');
          });
          if (record.direction === 'close') {
            clearNamedElements();
            clearTransitionRecord();
          }
        });
    });

    window.addEventListener('pageshow', function (event) {
      if (!event.persisted) return;
      var record = readTransitionRecord();
      if (record && record.direction === 'close') return;
      clearNamedElements();
      root.classList.remove('bh-pdp-transitioning');
      document.querySelectorAll('.is-bh-pdp-transitioning').forEach(function (element) {
        element.classList.remove('is-bh-pdp-transitioning');
      });
    });
  }

  function scheduleScrollRefresh() {
    if (!window.ScrollTrigger || scrollRefreshRaf) return;
    scrollRefreshRaf = requestAnimationFrame(function () {
      scrollRefreshRaf = null;
      window.ScrollTrigger.refresh();
    });
  }

  function refresh(scope) {
    var context = scope || document;
    initReveals(context);
    initMagnetic(context);
    if (motionAllowed() && selectWithin(context, '[data-bh-gsap]').length) {
      whenGsap().catch(function () {
        revealImmediately(context);
      });
    }
    scheduleScrollRefresh();
  }

  function flushMutationRoots() {
    var roots = mutationRoots.slice();
    mutationRoots.length = 0;
    mutationRaf = null;

    roots.forEach(function (node) {
      initReveals(node);
      initMagnetic(node);
    });
  }

  function queueMutationRoot(node) {
    mutationRoots.push(node);
    if (!mutationRaf) mutationRaf = requestAnimationFrame(flushMutationRoots);
  }

  function observeDynamicContent() {
    if (!('MutationObserver' in window)) return;
    var target = document.getElementById('MainContent');
    if (!target) return;

    new MutationObserver(function (records) {
      records.forEach(function (record) {
        record.addedNodes.forEach(function (node) {
          if (node.nodeType === 1) queueMutationRoot(node);
        });
      });
    }).observe(target, { childList: true, subtree: true });
  }

  function applyMotionState() {
    root.dataset.bhMotionIntensity = config.intensity || 'subtle';
    root.dataset.bhMobileMotion = String(config.mobileMotion !== false);
    root.classList.toggle('bh-motion-active', motionAllowed());
    root.classList.toggle('bh-motion-reduced', !motionAllowed());

    if (!motionAllowed()) {
      revealImmediately(document);
      if (lenis) lenis.stop();
    } else if (lenis) {
      lenis.start();
    }
  }

  window.BahimiMotion = {
    config: config,
    isReduced: function () {
      return !motionAllowed();
    },
    refresh: refresh,
    track: track,
    whenGsap: whenGsap,
    lenis: null
  };

  applyMotionState();
  refresh(document);
  initLenis();
  initAnalytics();
  initProductPageTransitions();
  observeDynamicContent();

  document.addEventListener('shopify:section:load', function (event) {
    refresh(event.target);
  });

  function onMediaChange(query, callback) {
    if (query.addEventListener) query.addEventListener('change', callback);
    else if (query.addListener) query.addListener(callback);
  }

  onMediaChange(reducedQuery, function () {
    applyMotionState();
    refresh(document);
  });
  onMediaChange(mobileQuery, function () {
    applyMotionState();
    refresh(document);
  });

  document.dispatchEvent(new CustomEvent('bahimi:motion-ready'));
})();
